"use client";
import { pxToVw } from "@/utils/pxToVw";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";
import FormTextFieldSimple from "../formulari/FormTextFieldSimple";
import { useActionState, useEffect, useState } from "react";
import { MOBILE_BASE } from "@/theme/theme";
import { getTextWithContext, useText, useTexts } from "@/context/TextContext";
import { registerCompleteSchema } from "@/lib/schemas/login";
import z from "zod";
import FormWrapper from "../FormWrapper";
import { registerCompleteAction } from "@/app/actions";

export default function RegisterCompleteForm() {
  const [data, setData] = useState({
    email: "",
    name: "",
    surname: "",
    companyName: "",
    customerCode: "",
    companyCIF: "",
    position: "",
    phone: "",
    authorization: false,
  });
  const [errors, setErrors] = useState({} as any);
  const [state, formAction, pending] = useActionState(registerCompleteAction, {
    errors: undefined,
  });
  const context = useTexts();

  useEffect(() => {
    const saved = sessionStorage.getItem("register");
    if (saved) {
      const { email } = JSON.parse(saved) as Partial<typeof data>;
      if (email) {
        setData((prev) => ({ ...prev, email }));
      }
    }
  }, []);

  useEffect(() => {
    if (state?.errors) {
      setErrors(state.errors);
    }
  }, [state]);

  const getFieldError = (fieldName: keyof typeof data) => {
    const fieldError = errors[fieldName];
    if (!fieldError) return "";
    return fieldError.errors[1]
      ? getTextWithContext(fieldError.errors[1], context)
      : getTextWithContext(fieldError.errors[0], context);
  };

  const validateField = (fieldName: keyof typeof data) => {
    const result = registerCompleteSchema.safeParse(data);

    const fieldErrors = result.success
      ? {}
      : (z.treeifyError(result.error).properties as any);

    // Mientras el campo esté vacío no se marca como erróneo; el "requerido"
    // se muestra al enviar el formulario.
    setErrors((prev: any) => ({
      ...prev,
      [fieldName]:
        data[fieldName] === "" ? undefined : fieldErrors?.[fieldName],
    }));
  };

  // Código de cliente y CIF: basta con rellenar uno de los dos. El aviso solo
  // se marca como error al enviar; al rellenar cualquiera de ellos se limpia.
  const validateCustomerCodeOrCIF = (
    fieldName: "customerCode" | "companyCIF",
  ) => {
    validateField(fieldName);
    if (data.customerCode.trim() || data.companyCIF.trim()) {
      setErrors((prev: any) => ({ ...prev, customerCodeOrCIF: undefined }));
    }
  };

  const handleChange = (value: any, fieldName: keyof typeof data) => {
    setData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  return (
    <Grid size={12}>
      <Typography
        variant="body3"
        color="marron"
        mb={{ xs: pxToVw(20, MOBILE_BASE), md: pxToVw(21) }}
        component={"h2"}
        fontSize={{
          xs: pxToVw(32, MOBILE_BASE),
          md: pxToVw(24, undefined, 32),
        }}
      >
        {useText("register.subtitle")}
      </Typography>
      <Typography
        variant="body5"
        color="warm2"
        mb={{ xs: pxToVw(12, MOBILE_BASE), md: pxToVw(12) }}
        component={"p"}
      >
        {useText("register.description")}
      </Typography>
      <Typography
        variant="body5"
        color="warm2"
        fontWeight={700}
        mb={{ xs: pxToVw(26, MOBILE_BASE), md: pxToVw(32) }}
        component={"p"}
      >
        {useText("register.requiredFieldsNotice")}
      </Typography>
      <Typography
        variant="body3"
        color="marron"
        mb={{ xs: pxToVw(28, MOBILE_BASE), md: pxToVw(28) }}
        fontSize={{
          xs: pxToVw(24, MOBILE_BASE),
          md: pxToVw(24, undefined, 32),
        }}
        component={"h2"}
      >
        {useText("register.userData")}
      </Typography>
      <FormWrapper action={formAction} errors={errors} pending={pending}>
        <Grid
          container
          spacing={{ xs: pxToVw(33, MOBILE_BASE), md: pxToVw(21) }}
        >
          <Grid size={{ xs: 12, md: 4 }}>
            <FormTextFieldSimple
              name="email"
              startAdornmentText={useText("register.form.emailLabel")}
              autoComplete="email"
              value={data.email}
              error={!!errors.email}
              helperText={getFieldError("email")}
              onBlur={() => validateField("email")}
              onChange={(e) => {
                handleChange(e.target.value, "email");
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormTextFieldSimple
              name="name"
              startAdornmentText={useText("register.form.nameLabel")}
              autoComplete="name"
              value={data.name}
              error={!!errors.name}
              helperText={getFieldError("name")}
              onBlur={() => validateField("name")}
              onChange={(e) => {
                handleChange(e.target.value, "name");
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormTextFieldSimple
              name="surname"
              startAdornmentText={useText("register.form.surnameLabel")}
              autoComplete="family-name"
              value={data.surname}
              error={!!errors.surname}
              helperText={getFieldError("surname")}
              onBlur={() => validateField("surname")}
              onChange={(e) => {
                handleChange(e.target.value, "surname");
              }}
            />
          </Grid>
        </Grid>
        <Grid
          container
          mt={{ xs: pxToVw(31, MOBILE_BASE), md: pxToVw(39) }}
          bgcolor={"#F6F5F5"}
          size={12}
          px={{ xs: pxToVw(16, MOBILE_BASE), md: pxToVw(16) }}
          py={{ xs: pxToVw(18, MOBILE_BASE), md: pxToVw(18) }}
          pb={{ xs: pxToVw(29, MOBILE_BASE), md: pxToVw(26) }}
        >
          <Typography
            variant="body5"
            color="marron"
            mb={{ xs: pxToVw(28, MOBILE_BASE), md: pxToVw(18) }}
          >
            {getTextWithContext("register.form.clientDescription", context)}
          </Typography>
          <Grid
            container
            size={12}
            spacing={pxToVw(16)}
            rowSpacing={{ xs: pxToVw(35, MOBILE_BASE), md: pxToVw(32) }}
          >
            <Grid size={{ xs: 12, md: 4 }}>
              <FormTextFieldSimple
                name="companyName"
                startAdornmentText={getTextWithContext(
                  "register.form.companyNameLabel",
                  context,
                )}
                autoComplete="organization"
                value={data.companyName}
                error={!!errors.companyName}
                helperText={getFieldError("companyName")}
                onBlur={() => validateField("companyName")}
                onChange={(e) => {
                  handleChange(e.target.value, "companyName");
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormTextFieldSimple
                name="customerCode"
                startAdornmentText={getTextWithContext(
                  "register.form.customerCodeLabel",
                  context,
                )}
                autoComplete="off"
                value={data.customerCode}
                error={!!errors.customerCode || !!errors.customerCodeOrCIF}
                helperText={getFieldError("customerCode")}
                onBlur={() => validateCustomerCodeOrCIF("customerCode")}
                onChange={(e) => {
                  handleChange(e.target.value, "customerCode");
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormTextFieldSimple
                name="companyCIF"
                startAdornmentText={getTextWithContext(
                  "register.form.companyCIFLabel",
                  context,
                )}
                autoComplete="off"
                value={data.companyCIF}
                error={!!errors.companyCIF || !!errors.customerCodeOrCIF}
                helperText={getFieldError("companyCIF")}
                onBlur={() => validateCustomerCodeOrCIF("companyCIF")}
                onChange={(e) => {
                  handleChange(e.target.value, "companyCIF");
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormTextFieldSimple
                name="position"
                startAdornmentText={getTextWithContext(
                  "register.form.positionLabel",
                  context,
                )}
                autoComplete="organization-title"
                value={data.position}
                error={!!errors.position}
                helperText={getFieldError("position")}
                onBlur={() => validateField("position")}
                onChange={(e) => {
                  handleChange(e.target.value, "position");
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormTextFieldSimple
                name="phone"
                startAdornmentText={getTextWithContext(
                  "register.form.phoneLabel",
                  context,
                )}
                autoComplete="tel"
                value={data.phone}
                error={!!errors.phone}
                helperText={getFieldError("phone")}
                onBlur={() => validateField("phone")}
                onChange={(e) => {
                  handleChange(e.target.value, "phone");
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid container mt={{ xs: pxToVw(30, MOBILE_BASE), md: pxToVw(30) }}>
          <FormControlLabel
            control={
              <Checkbox
                name="authorization"
                checked={data.authorization}
                onChange={(e) =>
                  handleChange(e.target.checked, "authorization")
                }
                sx={{
                  color: "#a89984",
                  "&.Mui-checked": {
                    color: "#472600",
                  },
                }}
              />
            }
            label={
              <Typography
                mt={{ xs: pxToVw(4, MOBILE_BASE), md: pxToVw(4) }}
                sx={{
                  color: "#9B8D82",
                  fontFamily: "var(--font-romek), sans-serif",
                  fontSize: {
                    xs: pxToVw(12, MOBILE_BASE),
                    md: pxToVw(16),
                  },
                  a: {
                    textDecoration: "underline",
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: getTextWithContext(
                    "register.form.authorizationText",
                    context,
                  ),
                }}
              ></Typography>
            }
          />
          {!!errors.authorization && (
            <Typography
              variant="body5"
              color="error"
              mt={pxToVw(8)}
              width={"100%"}
            >
              {getFieldError("authorization")}
            </Typography>
          )}
        </Grid>
        <Grid
          container
          pt={{ xs: pxToVw(23, MOBILE_BASE), md: pxToVw(43) }}
          mt={{ xs: pxToVw(40, MOBILE_BASE), md: pxToVw(0) }}
          borderTop={{ xs: "1px solid #D4D1C8", md: "none" }}
        >
          <Button
            variant="botonMarron"
            href={useText("register.form.backButtonLink")}
            id="button-register-volver"
            sx={{ mr: pxToVw(19) }}
          >
            {useText("register.form.backButton")}
          </Button>
          <Button
            variant="botonMarronInverso"
            id="button-register-enviar"
            type="submit"
          >
            {useText("register.form.submitButton")}
          </Button>
        </Grid>
      </FormWrapper>
    </Grid>
  );
}
